import { useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { useScrollDirection } from '@/hooks/useScrollDirection'
import AppBar from '@/components/AppBar'
import BottomNav from '@/components/BottomNav'
import i18n from '@/i18n'

// Routes that show a back button (detail/sub-pages)
const BACK_ROUTES: Record<string, string> = {
  '/invoices/': '/invoices',
  '/maintenance/new': '/maintenance',
  '/rewards/qr': '/rewards',
  '/rewards/catalog': '/rewards',
  '/rewards/redemptions': '/rewards',
  '/rewards/consent': '/rewards',
  '/vehicles/events': '/vehicles',
}

// Route-to-title map using i18n keys from common.nav namespace
const ROUTE_TITLES: Record<string, string> = {
  '/': 'nav.home',
  '/invoices': 'nav.invoices',
  '/maintenance': 'nav.maintenance',
  '/parcels': 'nav.parcels',
  '/vehicles': 'nav.vehicles',
  '/vehicles/events': 'nav.vehicles',
  '/rewards': 'nav.rewards',
  '/rewards/qr': 'nav.rewards',
  '/rewards/catalog': 'nav.rewards',
  '/rewards/redemptions': 'nav.rewards',
  '/rewards/consent': 'nav.rewards',
}

function getRouteTitle(pathname: string): string | null {
  // Exact match first
  if (ROUTE_TITLES[pathname]) return ROUTE_TITLES[pathname]
  // Sub-routes: /invoices/:id → invoices title, /maintenance/new → maintenance title
  if (pathname.startsWith('/invoices/')) return 'nav.invoices'
  if (pathname.startsWith('/maintenance/')) return 'nav.maintenance'
  if (pathname.startsWith('/rewards')) return 'nav.rewards'
  return null
}

function getBackPath(pathname: string): string | null {
  if (BACK_ROUTES[pathname]) return BACK_ROUTES[pathname]
  // Dynamic detail routes like /invoices/:id
  if (/^\/invoices\/\d+$/.test(pathname)) return '/invoices'
  // Rewards sub-routes
  if (pathname.startsWith('/rewards/')) return '/rewards'
  return null
}

export default function AppLayout() {
  const { logout } = useAuth()
  // Logout first unregisters the phone from push (up to a few seconds), so
  // the button shows it is working and cannot be pressed twice.
  const [loggingOut, setLoggingOut] = useState(false)
  const handleLogout = async () => {
    if (loggingOut) return
    setLoggingOut(true)
    try {
      await logout()
    } finally {
      setLoggingOut(false)
    }
  }
  const { t } = useTranslation('common')
  const location = useLocation()
  const navigate = useNavigate()
  const { isAtTop } = useScrollDirection()

  const toggleLanguage = () => {
    const next = i18n.language === 'th' ? 'en' : 'th'
    void i18n.changeLanguage(next)
    localStorage.setItem('liff_language', next)
  }

  const titleKey = getRouteTitle(location.pathname)
  const title = titleKey ? t(titleKey) : ''
  const backPath = getBackPath(location.pathname)
  const isHome = location.pathname === '/'

  return (
    <div className="min-h-screen bg-base-200">
      <div className="max-w-[430px] mx-auto bg-base-100 min-h-screen relative">
        {/* Sticky header: collapsible top strip + AppBar */}
        <div className="sticky top-0 z-30 pt-safe-chrome bg-primary">
          {/* Collapsible logout/language strip — visible only at top */}
          <div
            className={`bg-primary text-primary-content px-4 py-1.5 flex items-center justify-end gap-2 transition-all duration-200 overflow-hidden ${
              isAtTop ? 'max-h-10 opacity-100' : 'max-h-0 opacity-0 py-0'
            }`}
          >
            <span className="mr-auto text-[10px] font-bold uppercase tracking-wide bg-warning text-warning-content rounded-full px-2 py-0.5">
              {t('beta')}
            </span>
            <button
              className="btn btn-ghost btn-xs text-primary-content"
              onClick={toggleLanguage}
            >
              {i18n.language === 'th' ? 'EN' : 'TH'}
            </button>
            <button
              className="btn btn-ghost btn-xs text-primary-content"
              onClick={handleLogout}
              disabled={loggingOut}
              aria-busy={loggingOut}
              data-testid="logout-button"
            >
              {loggingOut && <span className="loading loading-spinner loading-xs" aria-hidden="true" />}
              {t('actions.logout')}
            </button>
          </div>

          {/* AppBar — hidden on home page (home has its own hero header) */}
          {!isHome && (
            <AppBar
              title={title}
              showBack={!!backPath}
              onBack={() => backPath && navigate(backPath)}
            />
          )}
        </div>

        {/* Page content — bottom padding clears BottomNav + safe area */}
        <div className="pb-[calc(56px+env(safe-area-inset-bottom))]">
          <Outlet />
        </div>

        <BottomNav />
      </div>
    </div>
  )
}
